import { Component } from "@theme/component";
import { isDesktopBreakpoint, observerElement, scrollToCenterHorizontal } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { syncDeferredMedia } from '@theme/media';

const TAB_SHIFT_DURATION = 200;

export class TabsComponent extends Component {
  static get observedAttributes() {
    return ["selected-index"];
  }

  #intersectionObserver = null;
  #tabShiftAnimations = [];
  #isHoverMode = () => this.getAttribute('data-action') === 'hover';
  #hasFadeAnimation = () => this.getAttribute('data-animation') === 'fade';
  #isMobileDisabled = () => !isDesktopBreakpoint() && this.getAttribute('data-mobile-action') === 'disabled';

  connectedCallback() {
    super.connectedCallback();
    this.init();
    this.setupIntersectionObserver();

    if (Shopify.designMode) {
      this.handleDesignMode();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeTabKeyboardListener();
    this._removeTabEventListeners();
    this.#cancelTabShift();
    this.#intersectionObserver?.disconnect();
  }

  /**
   * Re-wires tabs after the element is morphed in place (e.g. predictive search
   * results re-render). Without this, morph keeps the same element instance so
   * connectedCallback never re-runs, leaving newly rendered tabs without listeners.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#removeTabKeyboardListener();
    this._removeTabEventListeners();
    this.init();
  }

  #removeTabKeyboardListener() {
    if (this.boundHandleKeyboard) {
      this.removeEventListener('keydown', this.boundHandleKeyboard);
      this.boundHandleKeyboard = null;
    }
  }

  _removeTabEventListeners() {
    [...(this.tabEventHandlers || [])].forEach(({ tab, type, handler }) => {
      tab.removeEventListener(type, handler);
    });
    this.tabEventHandlers = null;
  }


  /**
   * Sets up intersection observer to preload tab content when near viewport
   * Improves performance by loading content before user switches tabs
   */
  setupIntersectionObserver() {
    this.#intersectionObserver = observerElement({
      element: this,
      callback: () => this.preloadAllTabContent(),
      threshold: '100'
    });
  }

  /**
   * Preloads all tab content
   * Unwraps template content and changes lazy images to eager loading
   */
  preloadAllTabContent() {
    this.panels.forEach((panel) => {
      const template = panel.querySelector('template[part="tabs-content__template"]');
      if (template && !panel.hasAttribute('data-loaded')) {
        panel.innerHTML = template.innerHTML;
        panel.setAttribute('data-loaded', 'true');
      }

      const images = panel.querySelectorAll('img[loading="lazy"]');
      images.forEach(img => {
        img.loading = 'eager';
        img.decode?.().catch(() => {});
      });
    });
  }

  /**
   * Initializes tabs component
   * Collects tab and panel elements and sets up event listeners
   */
  init() {
    this.tabs = Array.from(this.querySelectorAll('.tabs-nav__item'));
    this.panels = Array.from(this.querySelectorAll('.tabs-content__item'));
    this.viewAllButtons = this.querySelectorAll('.tabs__button');
    if (this.tabs.length === 0 || this.panels.length === 0) {
      return;
    }

    this.setupEventListeners();
  }

  /**
   * Handles Shopify theme editor design mode
   * Selects tab when block is selected in editor
   */
  handleDesignMode() {
    this.addEventListener('shopify:block:select', (e) => {
      if (!this.contains(e.target)) return;

      const tab = e.target.closest('.tabs-nav__item');
      const tabIndex = tab ? this.tabs.indexOf(tab) : -1;
      if (tabIndex >= 0) {
        this.setSelectedIndex(tabIndex);
        return;
      }

      const panel = e.target.closest('.tabs-content__item');
      const panelIndex = this.panels.indexOf(panel);
      if (panelIndex >= 0) this.setSelectedIndex(panelIndex);
    });
  }

  /**
   * Sets up click and keyboard event listeners for tabs
   * With data-action="hover": desktop = hover to switch (link click navigates), mobile = click to switch
   */
  setupEventListeners() {
    this.tabEventHandlers = [];

    this.tabs.forEach((tab, index) => {
      this.#addTabListener(tab, 'click', (e) => this.#handleTabClick(tab, index, e));

      if (this.#isHoverMode()) {
        this.#addTabListener(tab, 'mouseenter', (e) => this.#handleTabHover(index, e));
        this.#addTabListener(tab, 'mouseleave', (e) => this.#handleTabHoverLeave(index, e));
      }
    });

    this.boundHandleKeyboard = this.handleKeyboard.bind(this);
    this.addEventListener('keydown', this.boundHandleKeyboard);
  }

  #addTabListener(tab, type, handler) {
    this.tabEventHandlers.push({ tab, type, handler });
    tab.addEventListener(type, handler);
  }

  #handleTabClick(tab, index, e) {
    if (this.#isMobileDisabled()) return;
    const allowLinkNav = this.#isHoverMode() && isDesktopBreakpoint() && tab.tagName === 'A';
    if (allowLinkNav) return;
    if (tab.tagName === 'A') e.preventDefault();
    this.onTabClick?.(e, index);
    this.setSelectedIndex(index);
  }

  #handleTabHover(index, e) {
    this.onTabHoverEnter?.(e, index);
    if (isDesktopBreakpoint()) this.setSelectedIndex(index);
  }

  #handleTabHoverLeave(index, e) {
    this.onTabHoverLeave?.(e);
  }

  #syncPanelMedia(activeIndex) {
    this.panels.forEach((panel, index) => {
      panel.querySelectorAll('deferred-media').forEach((media) => {
        // Tabs only resume autoplay media and never lazy-load on activation.
        syncDeferredMedia(media, {
          isActive: index === activeIndex,
          shouldAutoplay: media.hasAttribute('data-autoplay'),
          canLoad: false,
          resumeNonAutoplay: false,
        });
      });
    });
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setSelectedIndex(index);
  }

  /**
   * Sets selected tab index with validation
   * Clamps index to valid range and animates transition
   * @param {number} index - Tab index to select
   */
  setSelectedIndex = async (index) => {
    const validIndex = Math.min(Math.max(index, 0), this.tabs.length - 1);

    await this.animateTabTransition(validIndex);
  }

  /**
   * Animates transition between tabs
   * Hydrates deferred panels first, then updates layout and animates new panel fade-in
   * @param {number} validIndex - Valid tab index
   */
  async animateTabTransition(validIndex) {
    this.preloadAllTabContent();

    const currentPanel = this.panels[this.selectedIndex];
    const newPanel = this.panels[validIndex];

    if (!currentPanel || !newPanel || currentPanel === newPanel) {
      this.updateLayout(validIndex);
      return;
    }

    this.updateLayout(validIndex);

    newPanel.querySelectorAll('carousel-component').forEach((carousel) => {
      if (carousel.swiper) carousel.swiper.update();
    });

    if (! this.#hasFadeAnimation()) {
      const keyframes = [{ transform: 'translateY(1.5rem)', opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }];
      await this.animatePanel(newPanel, keyframes);
    }
  }

  /**
   * Animates panel element with provided keyframes
   * @param {HTMLElement} element - Panel element to animate
   * @param {Array} keyframes - Animation keyframes
   * @returns {Promise} Animation finished promise
   */
  animatePanel(element, keyframes) {
    return element.animate(keyframes, {
      duration: 300,
      easing: 'ease-in-out',
      fill: 'none'
    }).finished;
  }

  /**
   * Updates tab and panel states, ARIA attributes, and view all buttons
   * Aligns selected tab on mobile if needed
   * @param {number} validIndex - Selected tab index
   */
  updateLayout(validIndex) {
    // Update attribute
    this.setAttribute('selected-index', validIndex.toString());

    // Update tabs
    this.#animateTabShift(() => {
      this.tabs.forEach((tab, buttonIndex) => {
        const isSelected = buttonIndex === validIndex;
        tab.setAttribute('aria-selected', isSelected.toString());
        tab.classList.toggle('btn--selected', isSelected);
      });
    });

    // Update panels
    this.panels.forEach((panel, panelIndex) => {
      const isSelected = panelIndex === validIndex;
      panel.setAttribute('aria-hidden', (!isSelected).toString());
      panel.classList.toggle('active', isSelected);
    });

    this.#syncPanelMedia(validIndex);

    // Update view all buttons
    this.updateViewAllButtons(this.tabs[validIndex]);

    this.alignSelectedTab(validIndex);

    // Trigger custom event
    this.dispatchEvent(new CustomEvent('tab-change', {
      detail: { selectedIndex: validIndex, selectedTab: this.tabs[validIndex], selectedPanel: this.panels[validIndex] }
    }));
  }

  // Replays the shift a selection change caused as a transform: only the "Icon" style
  // moves tabs, and transitioning its margin relayouts the row every frame, which judders
  #animateTabShift(applySelectedState) {
    const nav = this.tabs[0]?.closest('.tab-style--icon');

    if (!nav || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      applySelectedState();
      return;
    }

    const offsets = () => {
      const origin = nav.getBoundingClientRect().left - nav.scrollLeft;
      return this.tabs.map((tab) => tab.getBoundingClientRect().left - origin);
    };

    const before = offsets();
    this.#cancelTabShift();
    applySelectedState();

    this.#tabShiftAnimations = offsets().flatMap((offset, index) => {
      const delta = before[index] - offset;
      if (Math.abs(delta) < 0.5) return [];

      return this.tabs[index].animate(
        [{ translate: `${delta}px` }, { translate: '0px' }],
        { duration: TAB_SHIFT_DURATION, easing: 'ease-in-out' }
      );
    });
  }

  #cancelTabShift() {
    this.#tabShiftAnimations.forEach((animation) => animation.cancel());
    this.#tabShiftAnimations = [];
  }

  /**
   * Handles keyboard navigation for tabs
   * Arrow keys, Home, and End keys supported
   * @param {KeyboardEvent} event - Keyboard event
   */
  handleKeyboard(event) {
    const currentIndex = Array.from(this.tabs).indexOf(document.activeElement);

    if (currentIndex === -1) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.setSelectedIndex(currentIndex - 1);
        this.tabs[this.selectedIndex]?.focus();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.setSelectedIndex(currentIndex + 1);
        this.tabs[this.selectedIndex]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        this.setSelectedIndex(0);
        this.tabs[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        this.setSelectedIndex(this.tabs.length - 1);
        this.tabs[this.tabs.length - 1]?.focus();
        break;
    }
  }


  /**
   * Scrolls tab navigation to center selected tab on mobile/tablet
   * Only works if tabs overflow and align-selected-tab attribute is present
   * @param {number} newIndex - Index of newly selected tab
   */
  updateViewAllButtons(tab) {
    const link = tab?.getAttribute('data-link');
    this.viewAllButtons.forEach((button) => {
      if (link) {
        button.setAttribute('href', link);
        button.removeAttribute('aria-disabled');
      } else {
        button.removeAttribute('href');
        button.setAttribute('aria-disabled', 'true');
      }
    });
  }

  alignSelectedTab(newIndex) {
    if (isDesktopBreakpoint() || !this.hasAttribute('align-selected-tab')) {
      return;
    }

    const tabNav = this.querySelector('.tabs-nav__inner');
    const targetTab = this.tabs?.[newIndex];

    if (!tabNav || !targetTab) {
      return;
    }

    scrollToCenterHorizontal(tabNav, targetTab);
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}

export class ProductTabs extends TabsComponent {
  #labelTemplate = '';

  connectedCallback() {
    super.connectedCallback();
    this.viewAllButtons = this.querySelectorAll('.tabs__button');

    if (this.viewAllButtons.length > 0) {
      this.#labelTemplate = this.viewAllButtons[0].closest('.button-block')?.dataset.label?.trim() ?? '';
      const currentTab = this.tabs[this.selectedIndex];
      if (currentTab) this.updateViewAllButtons(currentTab);
    }
  }

  updateViewAllButtons(tab) {
    const link = tab?.getAttribute('data-link');
    const collectionTitle = tab?.getAttribute('data-collection-title') || '';
    const placeholder = this.#labelTemplate.match(/\[collection title\]/i)?.[0] ?? '';
    const upperCount = (placeholder.match(/[A-Z]/g) || []).length;
    const title = upperCount === 0 ? collectionTitle.toLowerCase() : collectionTitle;
    const label = placeholder ? this.#labelTemplate.replace(placeholder, title) : this.#labelTemplate;
    this.viewAllButtons.forEach((button) => {
      if (link) {
        button.setAttribute('href', link);
        button.removeAttribute('aria-disabled');
      } else {
        button.removeAttribute('href');
        button.setAttribute('aria-disabled', 'true');
      }
      const textEl = button.querySelector('.btn__text');
      if (textEl) textEl.textContent = label;
    });
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}

export class SearchResultsTabs extends TabsComponent {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

   /**
   * Handles filter update event
   */
   #handleFilterUpdate = () => {
    this._removeTabEventListeners();
    this.init();
  };
}

if (!customElements.get('search-results-tabs')) {
  customElements.define('search-results-tabs', SearchResultsTabs);
}

export class ScrollableTabs extends Component {
  connectedCallback() {
    super.connectedCallback();

    const tabs = Array.from(this.querySelectorAll('.tabs-nav__item'));
    if (tabs.length === 0) return;

    this.#scrollToSelected(tabs);
  }

  /**
   * Scrolls to the selected tab horizontally
   */
  #scrollToSelected(tabs) {
    const selectedTab = tabs.find(tab => tab.classList.contains('btn--selected'));
    if (!selectedTab) return;

    scrollToCenterHorizontal(this, selectedTab);
  }
}

if (!customElements.get('scrollable-tabs')) {
  customElements.define('scrollable-tabs', ScrollableTabs);
}